// "Preguntale a tu obra": resumen compacto del período cargado en el Calendario, para mandarle a Claude
// junto con la pregunta. Sale de las mismas fotos y detecciones (con zonas y correcciones de casco ya
// aplicadas) que muestra el calendario. ~3-6k tokens por mes.
import { photoStats, inWork, fmtHM, dayKey, listDays, addDays, arHour } from './iaData';
import { byDay } from './entregas';
import { wxKind, WX_KINDS } from './weather';
import { textoMateriales } from './entregasV3';

// Entregas del día para Claude: hora, vehículo, materiales nuevos y si el cliente la confirmó.
const VEH = { Camion: 'camión', 'Camion volquete': 'camión volquete', Mixer: 'mixer', Camioneta: 'camioneta', Material: null };
const entregaCorta = ({ v, confirmada, nota }) => {
  const o = { hora: fmtHM(v.arrive) };
  if (v.tipo !== 'material') o.hasta = fmtHM(v.leave);
  if (VEH[v.cls]) o.vehiculo = VEH[v.cls];
  if (v.materiales && v.materiales.length) o.materialesNuevos = textoMateriales(v.materiales);
  if (v.hormigonado) o.hormigonadoProbable = true;
  if (v.v3) o.confianzaIA = v.confianza; else o.soloCamion = true; // heurística vieja: no sabe qué material
  if (confirmada) o.confirmadaPorElCliente = true;
  if (nota) o.queLlego = nota;
  return o;
};

const DOW_LONG = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const dowOf = (k) => new Date(k + 'T12:00:00Z').getUTCDay();
const r1 = (x) => Math.round(x * 10) / 10;

// Agrupado por lo que la IA distingue con confianza: el tipo exacto de máquina (excavadora vs retro) todavía no.
const GRUPO = {
  Excavadora: 'maquinaria pesada', Retroexcavadora: 'maquinaria pesada',
  Camion: 'camión', 'Camion volquete': 'camión', Mixer: 'mixer (hormigonera)',
  Automovil: 'auto', Camioneta: 'camioneta',
};
const MIN_CONF_VEH = 0.4;

export const NO_DETECTA = [
  'tipo exacto de máquina (excavadora vs retroexcavadora, grúa, minicargadora, bobcat)',
  'materiales y entregas (qué llegó)',
  'avance de obra (pisos, m2, hormigonado terminado)',
  'quién es cada persona o de qué gremio',
  'lo que pasa fuera del encuadre de la cámara o fuera del horario de obra',
];

function daySummary(k, ph, cfg, wx, ent) {
  const out = { d: k, dow: DOW_LONG[dowOf(k)], fotos: ph.length };
  if (wx) {
    const kind = wxKind(wx.c, wx.p, { heavy: 10, minMm: 1 });
    out.clima = { cond: (WX_KINDS[kind] && WX_KINDS[kind].label) || kind, rain: r1(wx.p || 0), tmax: wx.max != null ? Math.round(wx.max) : null, tmin: wx.min != null ? Math.round(wx.min) : null };
  } else out.clima = null;
  if (!ph.length) return out;
  out.primeraFoto = fmtHM(ph[0].ms);
  out.ultimaFoto = fmtHM(ph[ph.length - 1].ms);
  let first = null, last = null, peak = 0, peakMs = null, sum = 0, withP = 0, helmet = 0, persons = 0, best = null, bestScore = -1;
  const byHour = {};
  const veh = {};
  for (const p of ph) {
    const s = photoStats(p, cfg);
    if (s.persons > 0) {
      if (first == null) first = p.ms;
      last = p.ms; sum += s.persons; withP += 1;
      const h = String(arHour(p.ms)).padStart(2, '0') + 'h'; // con letra: si no, JS ordena "10" antes que "08"
      byHour[h] = Math.max(byHour[h] || 0, s.persons);
    }
    if (s.persons > peak) { peak = s.persons; peakMs = p.ms; }
    persons += s.persons; helmet += s.helmet;
    const score = s.persons * 2 + s.machines;
    if (score > bestScore) { bestScore = score; best = p; }
    const seen = new Set();
    for (const d of s.dets) {
      const g = GRUPO[d.cls];
      if (g && d.conf >= MIN_CONF_VEH) seen.add(g);
    }
    for (const g of seen) {
      const v = veh[g] || (veh[g] = { n: 0, first: fmtHM(p.ms), last: null });
      v.n += 1; v.last = fmtHM(p.ms);
    }
  }
  if (first != null) {
    out.primeraPersona = fmtHM(first); out.ultimaPersona = fmtHM(last); out.horasJornada = r1((last - first) / 3600e3);
    out.picoPersonas = peak; out.horaPico = fmtHM(peakMs); out.promedioPersonas = r1(sum / withP);
    out.cascoPct = persons ? Math.min(100, Math.round((100 * helmet) / persons)) : null;
    out.personasPorHora = byHour;
  } else { out.primeraPersona = null; out.picoPersonas = 0; }
  if (Object.keys(veh).length) out.vehiculos = veh;
  // Hormigonado probable: un mixer que se queda (2 fotos o más, ~20 min) casi siempre es una colada.
  const mx = veh['mixer (hormigonera)'];
  if (mx && mx.n >= 2) out.hormigonadoProbable = { desde: mx.first, hasta: mx.last, fotosConMixer: mx.n };
  if (best && bestScore > 0) out.horaMasMovimiento = fmtHM(best.ms);
  if (ent && ent.length) out.entregas = ent.map(entregaCorta);
  return out;
}

const weekStart = (k) => addDays(k, -((dowOf(k) + 6) % 7));

export function buildDigest({ photos, cfg, wxDays, fromKey, toKey, name, today, entregas }) {
  const g = byDay(photos.filter((p) => inWork(p.ms, cfg)));
  const days = listDays(fromKey, toKey).map((k) => daySummary(k, g.get(k) || [], cfg, wxDays && wxDays.get(k), entregas && entregas[k]));
  const weeks = [];
  for (const d of days) {
    const ws = weekStart(d.d);
    let w = weeks[weeks.length - 1];
    if (!w || w.from !== ws) { w = { from: ws, to: addDays(ws, 6), daysLoaded: 0, workedDays: 0, hours: 0, peak: 0, peaks: [] }; weeks.push(w); }
    w.daysLoaded += 1;
    if (d.primeraPersona) { w.workedDays += 1; w.hours += d.horasJornada; w.peak = Math.max(w.peak, d.picoPersonas); w.peaks.push(d.picoPersonas); }
  }
  const trab = days.filter((d) => d.primeraPersona);
  const top = [...trab].sort((a, b) => b.picoPersonas - a.picoPersonas || b.promedioPersonas - a.promedioPersonas);
  const destacados = trab.length ? {
    diasTrabajados: trab.length,
    horasTotales: r1(trab.reduce((a, d) => a + d.horasJornada, 0)),
    diaConMasGente: { d: top[0].d, dow: top[0].dow, picoPersonas: top[0].picoPersonas, horaPico: top[0].horaPico, primeraPersona: top[0].primeraPersona },
    ranking5DiasConMasGente: top.slice(0, 5).map((d) => ({ d: d.d, picoPersonas: d.picoPersonas, horaPico: d.horaPico })),
    llegadaMasTemprana: [...trab].sort((a, b) => a.primeraPersona.localeCompare(b.primeraPersona))[0] && (({ d, primeraPersona }) => ({ d, primeraPersona }))([...trab].sort((a, b) => a.primeraPersona.localeCompare(b.primeraPersona))[0]),
    diasConHormigonadoProbable: days.filter((d) => d.hormigonadoProbable).map((d) => ({ d: d.d, dow: d.dow, ...d.hormigonadoProbable })),
    jornadaMasLarga: (({ d, horasJornada }) => ({ d, horasJornada }))([...trab].sort((a, b) => b.horasJornada - a.horasJornada)[0]),
  } : null;
  if (destacados && entregas) {
    destacados.entregas = days.filter((d) => d.entregas).flatMap((d) => d.entregas.map((e) => ({ d: d.d, dow: d.dow, ...e })));
  }
  const hoy = today || toKey;
  const lunes = weekStart(hoy);
  const semanas = {
    estaSemana: { desde: lunes, hasta: hoy },
    semanaPasada: { desde: addDays(lunes, -7), hasta: addDays(lunes, -1) },
  };
  return {
    camera: name || '',
    semanas,
    destacados,
    range: { from: fromKey, to: toKey },
    today,
    work: cfg.onlyWork ? `${cfg.workFrom}:00 a ${cfg.workTo}:00` : 'todo el día',
    days,
    weeks: weeks.map(({ peaks, ...w }) => ({ ...w, partial: w.daysLoaded < 7 && (w.from < fromKey || w.to > toKey), hours: r1(w.hours), avgPeak: peaks.length ? r1(peaks.reduce((a, b) => a + b, 0) / peaks.length) : 0 })),
    // Con entregas cargadas, "qué llegó" sí se puede responder (aproximado: lo que detecta la IA en la foto)
    notDetected: entregas ? NO_DETECTA.filter((x) => !x.startsWith('materiales y entregas')) : NO_DETECTA,
    ...(entregas ? { notaEntregas: 'Entregas detectadas por IA mirando una foto cada 20 min: hora aproximada; los materiales son los que quedaron a la vista (pallets, bolsones, madera, hierros, arena, piedra). Puede no ver descargas fuera de cuadro.' } : {}),
  };
}

// Foto más cercana a "HH:MM" de ese día (entre todas las fotos cargadas, dentro o fuera del horario).
export function nearestPhoto(photos, day, time) {
  const [h, m] = String(time).split(':').map(Number);
  const target = Date.parse(day + 'T00:00:00Z') + 3 * 3600e3 + ((h || 0) * 60 + (m || 0)) * 60e3;
  let best = null, bd = Infinity;
  for (const p of photos) {
    if (dayKey(p.ms) !== day) continue;
    const dd = Math.abs(p.ms - target);
    if (dd < bd) { bd = dd; best = p; }
  }
  return best && bd <= 3 * 3600e3 ? best : null;
}
